import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  IconButton,
  ListItemText,
  MenuItem,
  TextField,
  FormControl,
  InputLabel,
  LinearProgress,
  Select,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Save as SaveIcon,
  Send as SendIcon,
  Science as DryRunIcon,
  Add as AddIcon,
  ContentCopy as CopyIcon,
  AttachFile as AttachFileIcon,
  Refresh as RefreshIcon,
  Settings as SettingsIcon,
  AutoAwesome as AiIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
} from "@mui/icons-material";
import AddressbookPanelShell from "../../addressbook/panels/AddressbookPanelShell";
import { apiSendCampaign } from "../../../graphQL/campaign";
import { apiGetContactsCollection } from "../../../graphQL/addressbook/collections/contacts";
import { contactMatchesFilter } from "../../../utils/campaignContactFilter";
import { myToast } from "../../../libs";
import { v4 as uuid_v4 } from "../../../libs/uuid";
import config from "../../../config";
import { useUploadContext } from "../../../Provider/UploadContext";
import { apiCreateFile } from "../../../graphQL/file";
import { apiGetSenderAddresses, apiRequestSenderVerification } from "../../../graphQL/campaign";
import RemindersPanel from "../../addressbook/panels/RemindersPanel";
import CollapsibleCard from "../../../components/panels/CollapsibleCard";
import { useToggles } from "../../../contexts/FeatureToggleContext";
import CampaignCriteriaChat, { summarizeFilter, isEmptyFilter } from "./CampaignCriteriaChat";
import { apiGetSettings } from "../../../graphQL/settings";
import { useContactCategoryOptions } from "../../../components/component-contact/useContactCategories";
import {
  buildCampaignTracking,
  buildCampaignTrackedFileUrl,
} from "../../../utils/campaignShareUrl";

const CAMPAIGN_REF_COLLECTION = "campaigns";

const CAMPAIGN_STATUSES = ["Draft", "Active", "Processing", "Suspended"];
const CAMPAIGN_TYPES = [
  "Refinance",
  "Purchase",
  "Cash-Out Refinance",
  "SBA 504",
  "SBA 7(a)",
  "Bridge Loan",
  "Construction Financing",
];
const FILTER_MODE_OPTIONS = [
  { value: "AND", label: "Match ALL (AND)" },
  { value: "OR", label: "Match ANY (OR)" },
];

export { CAMPAIGN_TYPES };

const normalizeStatusList = (raw) => {
  if (Array.isArray(raw)) {
    return Array.from(
      new Set(raw.map((v) => String(v || "").trim()).filter(Boolean)),
    );
  }
  const s = String(raw || "").trim();
  if (!s) return [];
  return s
    .split(/[,;]+/)
    .map((v) => v.trim())
    .filter(Boolean);
};

const getFilter = (campaign) => {
  const data = campaign?.data && typeof campaign.data === "object" ? campaign.data : {};
  const filter = data?.contactFilter && typeof data.contactFilter === "object" ? data.contactFilter : {};
  return {
    query: String(filter?.query || ""),
    // `categories` (multi) is canonical; legacy campaigns carry a single `category`.
    categories: normalizeStatusList(filter?.categories ?? filter?.category),
    status: normalizeStatusList(filter?.status),
    mode: String(filter?.mode || "AND").toUpperCase() === "OR" ? "OR" : "AND",
    cities: Array.isArray(filter?.cities)
      ? filter.cities
      : String(filter?.cities || "").split(/[,\n;]+/).map((v) => v.trim()).filter(Boolean),
    zipcodes: Array.isArray(filter?.zipcodes)
      ? filter.zipcodes
      : String(filter?.zipcodes || "").split(/[,\n;]+/).map((v) => v.trim()).filter(Boolean),
  };
};

// Campaign types: `campaignTypes` (multi) is canonical; legacy campaigns may
// still carry a single `campaignType` string. Shared with CampaignListPanel.
export const getCampaignTypes = (campaign) => {
  const data = campaign?.data && typeof campaign.data === "object" ? campaign.data : {};
  if (Array.isArray(data.campaignTypes)) {
    return data.campaignTypes.map((v) => String(v || "").trim()).filter(Boolean);
  }
  const legacy = String(data.campaignType || "").trim();
  return legacy ? [legacy] : [];
};

const getEmail = (campaign) => {
  const data = campaign?.data && typeof campaign.data === "object" ? campaign.data : {};
  const email = data?.email && typeof data.email === "object" ? data.email : {};
  return {
    subject: String(email?.subject || ""),
    body: String(email?.body || ""),
    fromAddress: String(email?.fromAddress || ""),
    replyTo: String(email?.replyTo || ""),
    attachments: Array.isArray(email?.attachments) ? email.attachments : [],
  };
};

const parseListInput = (value, { zip = false } = {}) => {
  const parts = String(value || "")
    .split(/[,\n;\t]+/)
    .map((v) => v.trim())
    .filter(Boolean);

  if (!zip) return parts;

  return parts
    .map((v) => v.replace(/[^\d-]/g, "").split("-")[0].slice(0, 5))
    .filter((v) => /^\d{5}$/.test(v));
};

const uniqueList = (values) => Array.from(new Set(values.map((v) => String(v || "").trim()).filter(Boolean)));

// Small, on-brand status affordances for the collapsible card headers.
const CompleteBadge = () => (
  <CheckCircleIcon sx={{ fontSize: 15, color: "#16a34a", flexShrink: 0 }} />
);
const NeedsChip = ({ label }) => (
  <Chip
    size="small"
    label={label}
    sx={{
      height: 18,
      flexShrink: 0,
      fontSize: 10.5,
      fontWeight: 600,
      backgroundColor: "#E8EAF6",
      color: "#3949AB",
      "& .MuiChip-label": { px: 0.75 },
    }}
  />
);

// Mirrors the backend's SES html-vs-text detection (services/ses.ts) so the
// editor can truthfully show how the body will be sent.
const bodyLooksHtml = (body) =>
  /<(!doctype|html)[\s>]/i.test(String(body || "").slice(0, 1000))
  || /^\s*<(body|div|table|p|h[1-6])[\s>]/i.test(String(body || ""));

// Preview shows the template EXACTLY as authored — placeholders like
// {{ firstName }} stay literal so the user sees the real template, not
// substituted sample values (personalization happens only at send time).
const previewSrcDoc = (body) => String(body || "");

const CampaignDetailPanel = ({ campaign, dirty = false, dryRunning = false, sending = false, onChange, onSave, onActivate, onStop, onDryRun, onSend, onClose }) => {
  const authorId = useSelector((s) => s.auth.userId);
  const user = useSelector((s) => s.auth.user);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [htmlEditMode, setHtmlEditMode] = useState(false);
  const [confirmActivate, setConfirmActivate] = useState(false);
  const [lockedInfoOpen, setLockedInfoOpen] = useState(false);
  const [testSending, setTestSending] = useState(false);
  const [testAsOptions, setTestAsOptions] = useState([]);
  const [testAs, setTestAs] = useState(null);
  const filter = useMemo(() => getFilter(campaign), [campaign]);

  // Test sends may ONLY go to the Test Group (contacts in the "test" category)
  // so a test can never reach a real campaign recipient. Loaded independently
  // of the campaign's own audience filter.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setTestAsOptions([]);
      setTestAs(null);
      const domain = String(campaign?.domain || "").trim();
      if (!domain) return;
      try {
        const result = await apiGetContactsCollection({
          filters: { domain, category: "test" },
          pagination: { page: 1, limit: 100 },
          ordering: { field: "updatedAt", order: "Desc" },
        });
        if (cancelled) return;
        const items = (Array.isArray(result?.items) ? result.items : [])
          .filter((row) => String(row?.category || "").toLowerCase() === "test")
          .map((row) => {
            const person = row?.identity?.person || {};
            const label = [person.firstName, person.lastName].filter(Boolean).join(" ")
              || row?.identity?.company?.name || "(unnamed)";
            const primaryEmail = (row?.reach?.emails || []).find((e) => e?.isPrimary)?.value
              || (row?.reach?.emails || [])[0]?.value || "";
            return { id: String(row._id || row.id), label, email: String(primaryEmail).trim() };
          })
          .filter((o) => o.email);
        setTestAsOptions(items);
        if (items.length) setTestAs(items[0]);
      } catch {
        if (!cancelled) setTestAsOptions([]);
      }
    })();
    return () => { cancelled = true; };
  }, [campaign?.domain]);
  const email = useMemo(() => getEmail(campaign), [campaign]);
  // Channel: "email" (live) or "text" (SMS; editor + sample analytics only,
  // no sending backend yet). Stored in data.channel / data.sms.body.
  const channel = String(campaign?.data?.channel || "").toLowerCase() === "text" ? "text" : "email";
  const smsBody = String(campaign?.data?.sms?.body || "");
  const campaignTypes = useMemo(() => getCampaignTypes(campaign), [campaign]);
  const categoryOptions = useContactCategoryOptions(user?.id || user?.userId || authorId);
  const [statusOptions, setStatusOptions] = useState([]);
  const [cityInput, setCityInput] = useState("");
  const [zipcodeInput, setZipcodeInput] = useState("");
  const [copied, setCopied] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const uploadCtx = useUploadContext();
  const PrivateInfo = useSelector((store) => store.auth.PrivateInfo);
  const effectiveAuthorId = PrivateInfo?.id || authorId || "";
  const attachInputRef = useRef(null);
  const [attaching, setAttaching] = useState(false);
  const [confirmSend, setConfirmSend] = useState(false);
  const [confirmStopOpen, setConfirmStopOpen] = useState(false);
  const [senderAddresses, setSenderAddresses] = useState([]);
  const [senderDialogOpen, setSenderDialogOpen] = useState(false);
  const [senderInput, setSenderInput] = useState("");
  const [senderNotice, setSenderNotice] = useState(null); // { severity, text }
  const [senderBusy, setSenderBusy] = useState(false);

  const refreshSenderAddresses = async (domain) => {
    const d = String(domain || "").trim();
    if (!d) {
      setSenderAddresses([]);
      return;
    }
    try {
      const rows = await apiGetSenderAddresses(d);
      setSenderAddresses(Array.isArray(rows) ? rows : []);
    } catch (err) {
      console.error("Failed to load sender addresses:", err);
    }
  };

  useEffect(() => {
    refreshSenderAddresses(campaign?.domain);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaign?.domain]);
  // The tagged share URL is only stable once the campaign has a real (saved) id,
  // because cs_campaign embeds that id. For unsaved drafts we hide it and prompt
  // the user to save first (see render).
  const taggedShareUrl = useMemo(() => {
    const fileId = campaign?.data?.fileId;
    if (!fileId || !campaign?.id || campaign?._isNew) return "";
    try {
      return buildCampaignTrackedFileUrl(String(fileId), campaign);
    } catch {
      return "";
    }
  }, [campaign]);
  const status = String(campaign?.status || "").toLowerCase();
  const locked = ["active", "processing", "sent"].includes(status);
  const lockedReason = status === "sent"
    ? "This campaign has already been sent. Editing is locked to preserve what went out. Use Copy to start a new campaign from this one."
    : "This campaign is active. Stop it first (the red Stop button on the card) before making changes, or wait for it to finish. You can also Copy it into a new draft.";
  const canEdit = !locked;
  const isActive = ["active", "processing"].includes(status);
  const savedCampaign = Boolean(campaign?.id) && !campaign?._isNew;
  // Card / control visibility comes from the shared "Visible features"
  // toggles (Settings). With the advanced cards off, the panel becomes a
  // simple text-driven editor: name, audience chat, subject, body, Send.
  const { isOn } = useToggles();
  const showIdentity = isOn("campaign.section.identity");
  const showContactTypes = isOn("campaign.section.contactTypes");
  const showEmailSetup = isOn("campaign.section.emailSetup");
  const showEmailTools = isOn("campaign.section.emailTools");
  const showBrochure = isOn("campaign.section.brochure");
  const showAdvancedFilter = isOn("campaign.section.advancedFilter");
  const showReminders = isOn("campaign.section.reminders");
  const showDryRun = isOn("campaign.action.dryRun");
  // With the From row hidden the user cannot pick a sender, so default a
  // blank fromAddress to the domain's only verified sender address.
  const verifiedSenders = senderAddresses.filter((a) => a.verified).map((a) => a.email);
  const soleVerifiedSender = verifiedSenders.length === 1 ? verifiedSenders[0] : "";
  useEffect(() => {
    if (showEmailSetup || !canEdit || !soleVerifiedSender) return;
    if (email.fromAddress) return;
    setEmail({ fromAddress: soleVerifiedSender });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showEmailSetup, canEdit, soleVerifiedSender, email.fromAddress, campaign?.id]);
  const canSave = canEdit && (dirty || campaign?._isNew);
  // Dry run only marks recipients and sends no email, so the backend does not
  // require email subject/body. Gate it like Activate on save state (must be a
  // saved, non-dirty campaign) and never while a dry run is in flight.
  const canActivate = savedCampaign && canEdit && !dirty && Boolean(String(campaign?.domain || "").trim());
  const canDryRun = savedCampaign && !dirty && !dryRunning;
  const canSend =
    savedCampaign && !dirty && !sending && String(campaign?.status || "").toLowerCase() === "active" && channel === "email";

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const settingsDomain = String(user?.domain || "").trim();
      if (!settingsDomain) {
        if (!cancelled) setStatusOptions([]);
        return;
      }
      try {
        const res = await apiGetSettings({
          type: "addressbook",
          schemaType: "status",
          domain: settingsDomain,
        });
        const settings = Array.isArray(res?.settings) ? res.settings : [];
        const latest = settings.reduce((acc, cur) => {
          const accTs = Number(acc?.updatedAt ?? acc?.createdAt ?? 0);
          const curTs = Number(cur?.updatedAt ?? cur?.createdAt ?? 0);
          return curTs >= accTs ? cur : acc;
        }, settings[0] || null);
        const data = Array.isArray(latest?.data)
          ? latest.data
          : Array.isArray(latest?.data?.data)
            ? latest.data.data
            : [];
        const normalized = data
          .filter((d) => d && (d.isActive ?? d?.style?.isActive ?? true))
          .map((d) => String(d.value || d.name || d.label || "").trim())
          .filter(Boolean);
        if (!cancelled) {
          setStatusOptions(Array.from(new Set(normalized)).sort((a, b) => a.localeCompare(b)));
        }
      } catch (err) {
        console.error("Failed to load status settings:", err);
        if (!cancelled) setStatusOptions([]);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [user?.domain]);


  if (!campaign) {
    return (
      <AddressbookPanelShell collectionLabel="Campaign Details" onClose={onClose}>
        <Typography sx={{ color: "text.secondary", fontSize: 13, textAlign: "center", py: 4 }}>
          Select a campaign to view details.
        </Typography>
      </AddressbookPanelShell>
    );
  }

  const setField = (key, value) => {
    if (!canEdit) return;
    onChange?.({ ...campaign, [key]: value });
  };
  const setFilter = (updates) => {
    if (!canEdit) return;
    const currentData = campaign?.data && typeof campaign.data === "object" ? campaign.data : {};
    const { domain: _stripDomain, ...currentFilter } = currentData.contactFilter || {};
    const nextFilter = { ...currentFilter, ...filter, ...updates };
    delete nextFilter.domain;
    onChange?.({
      ...campaign,
      data: {
        ...currentData,
        contactFilter: nextFilter,
      },
    });
  };
  // Attachments reuse the app's existing file pipeline (UploadContext.uploadSingle
  // + apiCreateFile), same as PageFeedback's DataIngestFiles.
  const uploadAttachment = (file) =>
    new Promise((resolve) => {
      const fileId = uuid_v4();
      const directory = `${campaign.domain}/${campaign.id}/campaign-email`;
      const uploadKey = `${directory}/${fileId}`;
      const privateUrl = `${config.ENV.REACT_APP_HLS_BASE_URL}/${directory}/${fileId}`;
      uploadCtx.uploadSingle(
        directory,
        file,
        fileId,
        () => {},
        async (err) => {
          if (err) {
            myToast.error(err?.message || "Upload failed");
            resolve(null);
            return;
          }
          try {
            const doc = await apiCreateFile({
              authorId: effectiveAuthorId,
              fileCategory: "campaign-email",
              type: "Campaign Email Attachment",
              fileType: String(file.name || "").split(".").pop()?.trim().toLowerCase() || "",
              mimeType: file.type || "application/octet-stream",
              url: privateUrl,
              key: uploadKey,
              size: String(file.size),
              description: file.name,
              domain: campaign.domain || "",
              ref: { id: campaign.id, collection: "campaigns" },
              status: "active",
              isPublic: false,
            });
            resolve({
              fileId: doc.id,
              name: file.name,
              mimeType: file.type || "",
              size: file.size,
              url: privateUrl,
              key: uploadKey,
            });
          } catch (saveErr) {
            myToast.error(saveErr?.message || "Failed to attach file");
            resolve(null);
          }
        },
        { key: uploadKey, isPublic: false },
      );
    });

  const handleAttachPick = async (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = "";
    if (!picked.length || !savedCampaign || !canEdit) return;
    if (!effectiveAuthorId) {
      myToast.error("Missing author context. Please sign in again.");
      return;
    }
    setAttaching(true);
    const added = [];
    for (const file of picked) {
      // eslint-disable-next-line no-await-in-loop
      const entry = await uploadAttachment(file);
      if (entry) added.push(entry);
    }
    if (added.length) setEmail({ attachments: [...email.attachments, ...added] });
    setAttaching(false);
  };

  // Email content lives in the freeform data bag under `data.email` (no new
  // top-level campaign fields). This is UI-only; nothing here wires sending.
  // Generic write into the freeform data bag (no new top-level fields).
  const setDataField = (updates) => {
    if (!canEdit) return;
    const currentData = campaign?.data && typeof campaign.data === "object" ? campaign.data : {};
    onChange?.({ ...campaign, data: { ...currentData, ...updates } });
  };
  const setEmail = (updates) => {
    if (!canEdit) return;
    const currentData = campaign?.data && typeof campaign.data === "object" ? campaign.data : {};
    const nextEmail = { ...email, ...updates };
    onChange?.({
      ...campaign,
      data: {
        ...currentData,
        email: nextEmail,
      },
    });
  };
  // Apply a filter derived by the conversational criteria chat. Only the six
  // real contactFilter fields are written; `domain` is never accepted from the
  // chat, and provenance is stamped per the data conventions.
  const applyChatFilter = (clean) => {
    if (!canEdit) return;
    const currentData = campaign?.data && typeof campaign.data === "object" ? campaign.data : {};
    const { domain: _stripDomain, ...currentFilter } = currentData.contactFilter || {};
    const nextFilter = { ...currentFilter, ...clean };
    delete nextFilter.domain;
    // Chat emits the canonical `categories` (multi); drop any legacy single key.
    delete nextFilter.category;
    onChange?.({
      ...campaign,
      data: {
        ...currentData,
        contactFilter: nextFilter,
        contactFilterProvenance: {
          source: "campaign-criteria-chat",
          tool: "nl-to-contactfilter",
          updatedAt: new Date().toISOString(),
        },
      },
    });
    setChatOpen(false);
  };
  // Persist the brochure file id plus its derived campaign tracking metadata into
  // the freeform data bag (no new top-level campaign fields, per conventions).
  // The share URL is only materialized for a saved campaign, whose id is stable
  // and thus safe to embed as cs_campaign. Provenance is stamped on every write.
  const setBrochureFileId = (rawValue) => {
    if (!canEdit) return;
    const fileId = String(rawValue || "").trim();
    const currentData = campaign?.data && typeof campaign.data === "object" ? campaign.data : {};
    if (!fileId) {
      const { fileId: _f, tracking: _t, shareUrl: _s, taggedShareUrlProvenance: _p, ...rest } = currentData;
      onChange?.({ ...campaign, data: rest });
      return;
    }
    const nextData = { ...currentData, fileId };
    if (campaign?.id && !campaign?._isNew) {
      nextData.tracking = buildCampaignTracking(campaign);
      nextData.shareUrl = buildCampaignTrackedFileUrl(fileId, campaign);
      nextData.taggedShareUrlProvenance = {
        source: "campaign-detail-ui",
        tool: "campaign-tagged-share-url",
        updatedAt: new Date().toISOString(),
      };
    }
    onChange?.({ ...campaign, data: nextData });
  };
  const handleCopyShareUrl = () => {
    if (!taggedShareUrl) return;
    try {
      navigator.clipboard?.writeText(taggedShareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable; no-op */
    }
  };
  const addListItems = (key, rawValue, options = {}) => {
    if (!canEdit) return false;
    const parsed = parseListInput(rawValue, options);
    if (!parsed.length) return false;
    setFilter({ [key]: uniqueList([...(filter[key] || []), ...parsed]) });
    return true;
  };
  const removeListItem = (key, index) => {
    if (!canEdit) return;
    setFilter({ [key]: (filter[key] || []).filter((_, itemIndex) => itemIndex !== index) });
  };
  const handleListKeyDown = (event, key, value, clearValue, options = {}) => {
    if (!canEdit) return;
    if (!["Enter", ","].includes(event.key)) return;
    event.preventDefault();
    if (addListItems(key, value, options)) clearValue("");
  };

  // All campaign actions live in the panel header, each with a visible text
  // label (icon + word) so their purpose is clear. Gating and handlers are
  // unchanged from the prior icon-only buttons. The brochure share link is
  // surfaced here too, next to the actions.
  const actionButtonSx = ({ enabled, bg, hoverBg, color }) => ({
    textTransform: "none",
    fontSize: 11.5,
    fontWeight: 700,
    minWidth: 0,
    px: 1,
    py: 0.35,
    lineHeight: 1.2,
    borderRadius: 1.5,
    boxShadow: "none",
    backgroundColor: enabled ? bg : "#F1F5F9",
    color: enabled ? color : "#94A3B8",
    "&:hover": { boxShadow: "none", backgroundColor: enabled ? hoverBg : "#F1F5F9" },
    "&.Mui-disabled": { backgroundColor: "#F1F5F9", color: "#94A3B8" },
  });
  const headerActions = (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap", justifyContent: "flex-end" }}>
      <Tooltip title={
        campaign?._isNew
          ? "Save first to activate"
          : isActive
            ? "Active — toggle off to stop the campaign"
            : "Toggle on to activate (enrolls recipients; no email sent)"
      }>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Switch
            size="small"
            checked={isActive}
            disabled={isActive ? false : !canActivate}
            onChange={(e) => (e.target.checked ? setConfirmActivate(true) : setConfirmStopOpen(true))}
            sx={{ "& .Mui-checked": { color: "#0F766E" }, "& .Mui-checked + .MuiSwitch-track": { backgroundColor: "#0F766E" } }}
            inputProps={{ "aria-label": "Activate campaign" }}
          />
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: isActive ? "#0F766E" : "#94A3B8" }}>
            {isActive ? "Active" : "Activate"}
          </Typography>
        </Box>
      </Tooltip>
      {showDryRun ? (
      <Tooltip
        title={
          campaign?._isNew
            ? "Save first to dry run"
            : dirty
              ? "Save changes before dry run"
              : "Simulate send: marks recipients, sends no email"
        }
      >
        <span>
          <Button
            size="small"
            variant="contained"
            disabled={!canDryRun}
            onClick={() => onDryRun?.(campaign)}
            startIcon={<DryRunIcon sx={{ fontSize: 14 }} />}
            sx={actionButtonSx({ enabled: canDryRun, bg: "#E8EAF6", hoverBg: "#C5CAE9", color: "#3949AB" })}
            aria-label="Dry run campaign"
          >
            Dry run
          </Button>
        </span>
      </Tooltip>
      ) : null}
      <Tooltip
        title={
          !savedCampaign
            ? "Save first"
            : String(campaign?.status || "").toLowerCase() !== "active"
              ? "Activate the campaign first"
              : "Send real emails now"
        }
      >
        <span>
          <Button
            size="small"
            variant="contained"
            disabled={!canSend}
            onClick={() => setConfirmSend(true)}
            startIcon={<SendIcon sx={{ fontSize: 14 }} />}
            sx={actionButtonSx({ enabled: canSend, bg: "#3F51B5", hoverBg: "#3949AB", color: "#FFFFFF" })}
            aria-label="Send campaign"
          >
            Send
          </Button>
        </span>
      </Tooltip>
      <Tooltip
        title={
          locked
            ? "Locked — tap to learn why"
            : !canSave
              ? "No unsaved changes"
              : "Save"
        }
      >
        <span>
          <IconButton
            size="small"
            disabled={!locked && !canSave}
            onClick={() => (locked ? setLockedInfoOpen(true) : onSave?.(campaign))}
            aria-label="Save campaign"
            sx={{
              width: 26,
              height: 26,
              borderRadius: "50%",
              backgroundColor: (locked || canSave) ? "#16a34a" : "#F1F5F9",
              color: (locked || canSave) ? "#FFFFFF" : "#94A3B8",
              "&:hover": { backgroundColor: (locked || canSave) ? "#15803d" : "#F1F5F9" },
              "&.Mui-disabled": { backgroundColor: "#F1F5F9", color: "#94A3B8" },
            }}
          >
            <SaveIcon sx={{ fontSize: 15 }} />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );

  const normalizedStatus = String(campaign?.status || "");
  const normalizedDisplayStatus = normalizedStatus
    ? normalizedStatus.charAt(0).toUpperCase() + normalizedStatus.slice(1).toLowerCase()
    : "";
  const statusLabel = CAMPAIGN_STATUSES.includes(normalizedDisplayStatus)
    ? normalizedDisplayStatus
    : "Draft";

  const emailComplete = channel === "text"
    ? Boolean(smsBody.trim())
    : Boolean(email.subject.trim() && email.body.trim());
  const smsSegments = smsBody.length === 0 ? 0 : Math.ceil(smsBody.length / 160);
  const audienceSummary = isEmptyFilter(filter) ? "All contacts" : summarizeFilter(filter);

  const uploadHtmlButton = (
    <Button size="small" component="label" disabled={!canEdit} sx={{ textTransform: "none", fontSize: 11, py: 0 }}>
      Upload HTML…
      <input
        type="file"
        accept=".html,.htm,text/html"
        hidden
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (!file) return;
          const reader = new FileReader();
          reader.onload = () => {
            const content = String(reader.result || "");
            if (!bodyLooksHtml(content)) {
              myToast.error("This file is not recognizable as HTML and would be sent as plain text. Check the file.");
            }
            setEmail({ body: content });
            setHtmlEditMode(false);
          };
          reader.readAsText(file);
          e.target.value = "";
        }}
      />
    </Button>
  );

  return (
    <AddressbookPanelShell
      onClose={onClose}
      collectionLabel="Campaign Details"
      titleContent={
        showIdentity ? (
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: "text.primary" }}>
            Campaign Details
          </Typography>
        ) : (
          // Simple mode: the campaign name IS the panel title, edited in place.
          <TextField
            variant="standard"
            size="small"
            placeholder="Campaign name"
            disabled={!canEdit}
            value={campaign?.name || ""}
            onChange={(e) => setField("name", e.target.value)}
            inputProps={{ "aria-label": "Campaign name", style: { fontSize: 13, fontWeight: 600, padding: 0 } }}
            InputProps={{ disableUnderline: true }}
            fullWidth
            sx={{ flex: 1, minWidth: 160, "& .MuiInputBase-root": { color: "text.primary" } }}
          />
        )
      }
      headerActions={headerActions}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {/* Keyed by campaign so selecting a campaign re-collapses these two
            cards; new unsaved drafts open expanded for immediate editing. */}
        {showIdentity ? (
        <CollapsibleCard
          key={`identity-${campaign?.id || "new"}`}
          title="Campaign Identity"
          defaultExpanded={Boolean(campaign?._isNew)}
          statusIndicator={
            <Chip
              label={statusLabel}
              size="small"
              sx={{ fontWeight: 700, backgroundColor: "#EEF2FF", color: "#1E3A8A" }}
            />
          }
          summary={campaign?.name || "Untitled campaign"}
        >
        <Box sx={{ display: "flex", gap: 1 }}>
        <TextField
          label="Campaign Name"
          size="small"
          disabled={!canEdit}
          value={campaign?.name || ""}
          onChange={(e) => setField("name", e.target.value)}
          sx={{ flex: 1 }}
        />

        <FormControl size="small" disabled={!canEdit} sx={{ flex: 1 }}>
          <InputLabel id="campaign-type-label">Campaign Types</InputLabel>
          <Select
            labelId="campaign-type-label"
            label="Campaign Types"
            multiple
            value={campaignTypes}
            onChange={(e) => {
              const v = e.target.value;
              const nextTypes = (Array.isArray(v) ? v : [v]).filter(Boolean);
              const currentData = campaign?.data && typeof campaign.data === "object" ? campaign.data : {};
              // `campaignTypes` (multi) is canonical; drop the legacy single
              // `campaignType` key so the two never disagree.
              const { campaignType: _drop, campaignTypes: _drop2, ...rest } = currentData;
              onChange?.({
                ...campaign,
                data: nextTypes.length ? { ...rest, campaignTypes: nextTypes } : rest,
              });
            }}
            renderValue={(selected) => {
              if (!selected || selected.length === 0) return <em>(none)</em>;
              return (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip key={value} size="small" label={value} />
                  ))}
                </Box>
              );
            }}
          >
            {CAMPAIGN_TYPES.map((t) => (
              <MenuItem key={t} value={t}>
                <Checkbox size="small" checked={campaignTypes.indexOf(t) > -1} />
                <ListItemText primary={t} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        </Box>

        </CollapsibleCard>
        ) : null}

        {!showAdvancedFilter ? (
        <CollapsibleCard
          key={`audience-${campaign?.id || "new"}`}
          title="Who gets it"
          defaultExpanded
          statusIndicator={isEmptyFilter(filter) ? <NeedsChip label="Everyone" /> : <CompleteBadge />}
          summary={audienceSummary}
        >
          <Box sx={{ mx: -1, mb: -1, border: "1px solid #E8EAF6", borderRadius: 1, overflow: "hidden" }}>
            <CampaignCriteriaChat
              domain={String(campaign?.domain || "").trim()}
              authorId={authorId}
              initialFilter={filter}
              onApply={applyChatFilter}
              inline
              applied={!isEmptyFilter(filter)}
            />
          </Box>
        </CollapsibleCard>
        ) : null}

        {showContactTypes ? (
        <CollapsibleCard
          key={`contact-types-${campaign?.id || "new"}`}
          title="Contact Types Filter"
          defaultExpanded={Boolean(campaign?._isNew)}
          statusIndicator={
            filter.categories.length > 0 ? <CompleteBadge /> : <NeedsChip label="All contacts" />
          }
          summary={filter.categories.length > 0 ? filter.categories.join(", ") : "All contacts"}
        >
        <FormGroup sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", columnGap: 1 }}>
          <FormControlLabel
            disabled={!canEdit}
            control={
              <Checkbox
                size="small"
                checked={filter.categories.length === 0}
                onChange={() => {
                  if (filter.categories.length > 0) setFilter({ categories: [], category: undefined });
                }}
              />
            }
            label="All"
            sx={{ mr: 0, "& .MuiFormControlLabel-label": { fontSize: 12.5 } }}
          />
          {categoryOptions.map((option) => (
            <FormControlLabel
              key={option}
              disabled={!canEdit}
              control={
                <Checkbox
                  size="small"
                  checked={filter.categories.indexOf(option) > -1}
                  onChange={(e) => {
                    const next = e.target.checked
                      ? [...filter.categories, option]
                      : filter.categories.filter((c) => c !== option);
                    // Write the multi shape and clear the legacy single key.
                    setFilter({ categories: next, category: undefined });
                  }}
                />
              }
              label={option}
              sx={{ mr: 0, "& .MuiFormControlLabel-label": { fontSize: 12.5 } }}
            />
          ))}
        </FormGroup>
        </CollapsibleCard>
        ) : null}

        <CollapsibleCard
          title={channel === "text" ? "Text message" : "Email"}
          defaultExpanded
          statusIndicator={
            emailComplete ? <CompleteBadge /> : <NeedsChip label={channel === "text" ? "Needs a message" : "Optional for dry run"} />
          }
          summary={
            emailComplete
              ? (channel === "text" ? smsBody.slice(0, 80) : email.subject)
              : (channel === "text" ? "Needs a message" : "Needs subject & body to send")
          }
          headerActions={
            <ToggleButtonGroup
              size="small"
              exclusive
              value={channel}
              disabled={!canEdit}
              onChange={(e, v) => { if (v) setDataField({ channel: v }); }}
              aria-label="Campaign channel"
              sx={{ "& .MuiToggleButton-root": { textTransform: "none", fontSize: 11.5, py: 0.25, px: 1.25 }, "& .Mui-selected": { backgroundColor: "#E8EAF6 !important", color: "#283593 !important", fontWeight: 700 } }}
            >
              <ToggleButton value="email">Email</ToggleButton>
              <ToggleButton value="text">Text</ToggleButton>
            </ToggleButtonGroup>
          }
        >
        {channel === "text" ? (
          <>
            <TextField
              fullWidth
              label="Message"
              size="small"
              multiline
              minRows={4}
              disabled={!canEdit}
              value={smsBody}
              onChange={(e) => setDataField({ sms: { ...(campaign?.data?.sms || {}), body: e.target.value } })}
              placeholder="Hi {{ firstName }}, your loan on {{ property }} comes due soon. Want to look at refinance options? Reply STOP to opt out."
              InputProps={{ sx: { fontSize: 13, alignItems: "flex-start" } }}
            />
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
              <Typography sx={{ fontSize: 11, color: "#5A6182" }}>
                {smsBody.length} characters, {smsSegments} {smsSegments === 1 ? "text" : "texts"} per recipient
              </Typography>
              <Box sx={{ flex: 1 }} />
              <Chip size="small" label="Not connected yet" sx={{ backgroundColor: "#FFF8E1", color: "#8D6E63", fontWeight: 700, fontSize: 10.5 }} />
            </Box>
            {smsBody.trim() ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 0.5 }}>
                <Box sx={{ width: 260, p: 1.25, borderRadius: 3, border: "1px solid #E8EAF6", backgroundColor: "#F4F5FB" }}>
                  <Box sx={{ maxWidth: "88%", px: 1.25, py: 0.75, borderRadius: 2, borderBottomLeftRadius: 4, backgroundColor: "#FFFFFF", border: "1px solid #E8EAF6", fontSize: 12.5, color: "#1A1F36", whiteSpace: "pre-wrap" }}>
                    {smsBody}
                  </Box>
                </Box>
              </Box>
            ) : null}
          </>
        ) : null}
        {channel === "email" ? (
        <>
        {showEmailSetup ? (
        <>
        <Box sx={{ display: "flex", gap: 0.75, alignItems: "center" }}>
          <FormControl fullWidth size="small" disabled={!canEdit} sx={{ flex: 1 }}>
            <InputLabel id="campaign-from-label">From Address</InputLabel>
            <Select
              labelId="campaign-from-label"
              label="From Address"
              value={email.fromAddress || ""}
              onChange={(e) => setEmail({ fromAddress: e.target.value })}
            >
              <MenuItem value="">(none)</MenuItem>
              {senderAddresses
                .filter((a) => a.verified)
                .map((a) => (
                  <MenuItem key={a.email} value={a.email}>
                    {a.email}
                  </MenuItem>
                ))}
              {email.fromAddress &&
              !senderAddresses.some((a) => a.verified && a.email === email.fromAddress) ? (
                <MenuItem value={email.fromAddress}>{email.fromAddress}</MenuItem>
              ) : null}
            </Select>
          </FormControl>
          <Tooltip title="Sender addresses">
            <IconButton
              size="small"
              onClick={() => setSenderDialogOpen(true)}
              sx={{ width: 30, height: 30, color: "#3F51B5" }}
              aria-label="Sender addresses"
            >
              <SettingsIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>
        <TextField
          fullWidth
          label="Reply-To (optional)"
          size="small"
          disabled={!canEdit}
          value={email.replyTo}
          onChange={(e) => setEmail({ replyTo: e.target.value })}
          placeholder="name@example.com"
        />
        </>
        ) : null}
        <TextField
          fullWidth
          label="Subject"
          size="small"
          disabled={!canEdit}
          value={email.subject}
          onChange={(e) => setEmail({ subject: e.target.value })}
        />
        {bodyLooksHtml(email.body) && !htmlEditMode ? (
          <Box sx={{ border: "1px solid #C5CAE9", borderRadius: 1, overflow: "hidden" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, px: 1, py: 0.5, backgroundColor: "#E8EAF6" }}>
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#283593" }}>
                Sends as: HTML email — recipients see this rendering
              </Typography>
              <Box sx={{ flex: 1 }} />
              {showEmailTools ? uploadHtmlButton : null}
              <Button size="small" disabled={!canEdit} sx={{ textTransform: "none", fontSize: 11, py: 0 }} onClick={() => setHtmlEditMode(true)}>
                {showEmailTools ? "Edit HTML" : "Edit"}
              </Button>
            </Box>
            <iframe
              title="Email body preview"
              sandbox=""
              style={{ width: "100%", height: 340, border: 0, background: "#fff", display: "block" }}
              srcDoc={previewSrcDoc(email.body)}
            />
          </Box>
        ) : (
          <>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              {bodyLooksHtml(email.body) && (
                <>
                  <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#283593" }}>Editing HTML source</Typography>
                  <Button size="small" sx={{ textTransform: "none", fontSize: 11, py: 0 }} onClick={() => setHtmlEditMode(false)}>
                    Done — show rendering
                  </Button>
                </>
              )}
              {!bodyLooksHtml(email.body) && email.body.trim() && (
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#92400E" }}>
                  Sends as: plain text (no HTML detected)
                </Typography>
              )}
              <Box sx={{ flex: 1 }} />
              {showEmailTools ? uploadHtmlButton : null}
            </Box>
            <TextField
              fullWidth
              label="Body"
              size="small"
              multiline
              minRows={5}
              disabled={!canEdit}
              value={email.body}
              onChange={(e) => setEmail({ body: e.target.value })}
              InputProps={{ sx: { fontFamily: "monospace", fontSize: 12.5, alignItems: "flex-start", "& textarea": { resize: "vertical" } } }}
            />
          </>
        )}
        {showEmailTools ? (
        <>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
          <Button
            size="small"
            variant="outlined"
            disabled={!email.body.trim()}
            sx={{ textTransform: "none" }}
            onClick={() => setPreviewOpen(true)}
          >
            Preview
          </Button>
          <Autocomplete
            size="small"
            options={testAsOptions}
            value={testAs}
            onChange={(e, v) => setTestAs(v)}
            getOptionLabel={(o) => (o?.email ? `${o.label} <${o.email}>` : o?.label || "")}
            isOptionEqualToValue={(o, v) => o?.id === v?.id}
            renderInput={(params) => <TextField {...params} label="Send test to a contact" placeholder="pick a contact" />}
            sx={{ minWidth: 300 }}
          />
          <Button
            size="small"
            variant="outlined"
            disabled={testSending || !testAs?.email || !campaign?.id || campaign?._isNew || dirty}
            sx={{ textTransform: "none" }}
            onClick={async () => {
              setTestSending(true);
              try {
                const res = await apiSendCampaign({ id: campaign.id, dryRun: false, testTo: testAs.email, testContactId: testAs.id });
                if (res?.sent === 1) myToast.success(`Test email sent to ${testAs.label} <${testAs.email}>`);
                else myToast.error("Test send did not report success");
              } catch (err) {
                myToast.error(err?.message || "Test send failed");
              } finally {
                setTestSending(false);
              }
            }}
          >
            {testSending ? "Sending…" : "Send test email"}
          </Button>
          {dirty && (
            <Typography sx={{ fontSize: 11, color: "#92400E" }}>Save first to test the latest body</Typography>
          )}
        </Box>
        <Typography sx={{ fontSize: 11, color: "#64748B" }}>
          The test goes to the selected contact&apos;s own email, personalized with their real name and properties.
        </Typography>
        </>
        ) : null}

        </>
        ) : null}

        <Dialog open={lockedInfoOpen} onClose={() => setLockedInfoOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ fontSize: 15, fontWeight: 700 }}>Editing is locked</DialogTitle>
          <DialogContent>
            <Typography sx={{ fontSize: 13 }}>{lockedReason}</Typography>
          </DialogContent>
          <DialogActions>
            <Button size="small" variant="contained" sx={{ textTransform: "none" }} onClick={() => setLockedInfoOpen(false)}>
              Got it
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={confirmActivate} onClose={() => setConfirmActivate(false)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ fontSize: 15, fontWeight: 700 }}>Activate campaign?</DialogTitle>
          <DialogContent>
            <Typography sx={{ fontSize: 13, mb: 1 }}>
              Activating enrolls every contact matching this campaign&apos;s filter
              ({audienceSummary}) as recipients and marks the campaign Active.
            </Typography>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#0F766E" }}>
              No emails are sent by activating. Sending is a separate step.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button size="small" onClick={() => setConfirmActivate(false)} sx={{ textTransform: "none" }}>
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              sx={{ textTransform: "none", backgroundColor: "#0F766E" }}
              onClick={() => { setConfirmActivate(false); onActivate?.(campaign); }}
            >
              Activate
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle sx={{ fontSize: 15, fontWeight: 700 }}>Email preview</DialogTitle>
          <DialogContent sx={{ p: 0, height: "70vh" }}>
            <iframe
              title="Email preview"
              sandbox=""
              style={{ width: "100%", height: "100%", border: 0, background: "#fff" }}
              srcDoc={previewSrcDoc(email.body)}
            />
          </DialogContent>
          <DialogActions>
            <Button size="small" onClick={() => setPreviewOpen(false)} sx={{ textTransform: "none" }}>Close</Button>
          </DialogActions>
        </Dialog>
        {showEmailTools && channel === "email" ? (
        <Typography sx={{ fontSize: 11, color: "#3949AB" }}>
          Variables: {"{{ firstName }} {{ lastName }} {{ fullName }} {{ company }} {{ email }}"}
        </Typography>
        ) : null}
        {channel === "email" && (showEmailTools || email.attachments.length) ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
          {email.attachments.map((a) => (
            <Chip
              key={a.fileId}
              size="small"
              label={a.name}
              onDelete={
                canEdit
                  ? () => setEmail({ attachments: email.attachments.filter((x) => x.fileId !== a.fileId) })
                  : undefined
              }
            />
          ))}
          <Tooltip title={savedCampaign ? "" : "Save the campaign first"}>
            <span>
              <Button
                size="small"
                startIcon={<AttachFileIcon sx={{ fontSize: 15 }} />}
                disabled={!canEdit || !savedCampaign || attaching}
                onClick={() => attachInputRef.current?.click()}
                sx={{
                  textTransform: "none",
                  fontSize: 11.5,
                  fontWeight: 600,
                  color: "#3F51B5",
                  "&:hover": { backgroundColor: "#E8EAF6" },
                }}
              >
                Attach files
              </Button>
            </span>
          </Tooltip>
          <input ref={attachInputRef} type="file" multiple hidden onChange={handleAttachPick} />
        </Box>
        ) : null}
        {attaching ? <LinearProgress sx={{ borderRadius: 1 }} /> : null}
        </CollapsibleCard>

        {showBrochure ? (
        <CollapsibleCard
          title="Brochure Link"
          defaultExpanded={false}
          statusIndicator={
            campaign?.data?.fileId ? <CompleteBadge /> : <NeedsChip label="None" />
          }
          summary={String(campaign?.data?.fileId || "") || "No brochure"}
        >
        <TextField
          fullWidth
          label="Public File ID (brochure)"
          size="small"
          disabled={!canEdit}
          value={String(campaign?.data?.fileId || "")}
          placeholder="e.g. the public file id this campaign drives traffic to"
          onChange={(e) => setBrochureFileId(e.target.value)}
        />
        {campaign?.data?.fileId ? (
          savedCampaign ? (
            <Box
              sx={{
                mt: 0.25,
                p: 1,
                borderRadius: 1,
                backgroundColor: "#F4F5FB",
                border: "1px solid #E8EAF6",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Typography
                sx={{
                  flex: 1,
                  fontSize: 11,
                  fontFamily: "monospace",
                  color: "#1A1F36",
                  wordBreak: "break-all",
                }}
              >
                {taggedShareUrl}
              </Typography>
              <Tooltip title={copied ? "Copied" : "Copy tagged share URL"}>
                <IconButton
                  size="small"
                  onClick={handleCopyShareUrl}
                  sx={{
                    width: 26,
                    height: 26,
                    color: "#FFFFFF",
                    backgroundColor: "#3F51B5",
                    "&:hover": { backgroundColor: "#3949AB" },
                  }}
                  aria-label="Copy tagged share URL"
                >
                  <CopyIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            </Box>
          ) : (
            <Typography sx={{ fontSize: 11, color: "#3949AB", mt: 0.25 }}>
              Save the campaign to generate its stable tagged share URL.
            </Typography>
          )
        ) : null}
        </CollapsibleCard>
        ) : null}

        {showAdvancedFilter ? (
        <CollapsibleCard
          title="Advanced Filter"
          defaultExpanded={false}
          statusIndicator={
            filter.query || filter.status.length || filter.cities.length || filter.zipcodes.length
              ? <CompleteBadge />
              : <NeedsChip label="None" />
          }
          summary={audienceSummary}
          headerActions={
            <Button
              size="small"
              startIcon={<AiIcon sx={{ fontSize: 15 }} />}
              disabled={!canEdit}
              onClick={() => setChatOpen(true)}
              sx={{
                textTransform: "none",
                fontSize: 11.5,
                fontWeight: 600,
                color: "#3F51B5",
                "&:hover": { backgroundColor: "#E8EAF6" },
              }}
            >
              Build with chat
            </Button>
          }
        >
        <FormControl fullWidth size="small">
          <InputLabel id="campaign-filter-mode-label">Logic</InputLabel>
          <Select
            labelId="campaign-filter-mode-label"
            label="Logic"
            disabled={!canEdit}
            value={filter.mode}
            onChange={(e) => setFilter({ mode: e.target.value })}
          >
            {FILTER_MODE_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          fullWidth
          label="Search Text"
          size="small"
          disabled={!canEdit}
          value={filter.query}
          onChange={(e) => setFilter({ query: e.target.value })}
          placeholder="Name, company, email, notes..."
        />

        <FormControl fullWidth size="small" disabled={!canEdit}>
          <InputLabel id="campaign-status-label">Contact Status</InputLabel>
          <Select
            labelId="campaign-status-label"
            label="Contact Status"
            multiple
            value={filter.status}
            onChange={(e) => {
              const v = e.target.value;
              setFilter({ status: Array.isArray(v) ? v : normalizeStatusList(v) });
            }}
            renderValue={(selected) => {
              if (!selected || selected.length === 0) return <em>Any</em>;
              return (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip key={value} size="small" label={value} />
                  ))}
                </Box>
              );
            }}
          >
            {(statusOptions.length > 0
              ? statusOptions
              : ["active", "inactive", "lead", "prospect", "client"]
            ).map((option) => (
              <MenuItem key={option} value={option}>
                <Checkbox
                  size="small"
                  checked={filter.status.indexOf(option) > -1}
                />
                <ListItemText primary={option} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
          <Box sx={{ display: "flex", gap: 0.75, alignItems: "flex-start" }}>
            <TextField
              fullWidth
              size="small"
              label="Cities"
              disabled={!canEdit}
              value={cityInput}
              onChange={(e) => setCityInput(e.target.value)}
              onKeyDown={(e) => handleListKeyDown(e, "cities", cityInput, setCityInput)}
              placeholder="Type a city and press Enter, or paste comma-separated list"
            />
            <Tooltip title="Add city">
              <span>
                <IconButton
                  size="small"
                  aria-label="Add city"
                  disabled={!canEdit || !cityInput.trim()}
                  onClick={() => {
                    if (addListItems("cities", cityInput)) setCityInput("");
                  }}
                  sx={{ width: 34, height: 34, border: "1px solid #CBD5E1" }}
                >
                  <AddIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
          {filter.cities.length ? (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {filter.cities.map((city, index) => (
                <Chip
                  size="small"
                  label={city}
                  onDelete={canEdit ? () => removeListItem("cities", index) : undefined}
                  key={`city-${city}-${index}`}
                  sx={{
                    backgroundColor: "#DBEAFE",
                    color: "#1D4ED8",
                    fontWeight: 700,
                    "& .MuiChip-deleteIcon": { color: "#2563EB" },
                    "& .MuiChip-deleteIcon:hover": { color: "#1E40AF" },
                  }}
                />
              ))}
            </Box>
          ) : null}
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
          <Box sx={{ display: "flex", gap: 0.75, alignItems: "flex-start" }}>
            <TextField
              fullWidth
              size="small"
              label="Zipcodes"
              disabled={!canEdit}
              value={zipcodeInput}
              onChange={(e) => setZipcodeInput(e.target.value)}
              onKeyDown={(e) => handleListKeyDown(e, "zipcodes", zipcodeInput, setZipcodeInput, { zip: true })}
              placeholder="Type a 5-digit zip and press Enter"
            />
            <Tooltip title="Add zipcode">
              <span>
                <IconButton
                  size="small"
                  aria-label="Add zipcode"
                  disabled={!canEdit || !parseListInput(zipcodeInput, { zip: true }).length}
                  onClick={() => {
                    if (addListItems("zipcodes", zipcodeInput, { zip: true })) setZipcodeInput("");
                  }}
                  sx={{ width: 34, height: 34, border: "1px solid #CBD5E1" }}
                >
                  <AddIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
          {filter.zipcodes.length ? (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {filter.zipcodes.map((zip, index) => (
                <Chip
                  size="small"
                  label={zip}
                  onDelete={canEdit ? () => removeListItem("zipcodes", index) : undefined}
                  key={`zip-${zip}-${index}`}
                  sx={{
                    backgroundColor: "#DBEAFE",
                    color: "#1D4ED8",
                    fontWeight: 700,
                    "& .MuiChip-deleteIcon": { color: "#2563EB" },
                    "& .MuiChip-deleteIcon:hover": { color: "#1E40AF" },
                  }}
                />
              ))}
            </Box>
          ) : null}
        </Box>
        </CollapsibleCard>
        ) : null}

        {savedCampaign && showReminders ? (
          <CollapsibleCard title="Reminders" defaultExpanded={false}>
            <RemindersPanel
              authorId={authorId}
              refCollection={CAMPAIGN_REF_COLLECTION}
              refId={campaign.id}
            />
          </CollapsibleCard>
        ) : null}
      </Box>

      <Dialog
        open={chatOpen}
        onClose={() => setChatOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 2, overflow: "hidden" } }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            py: 1.25,
            px: 1.75,
            backgroundColor: "#3F51B5",
            color: "#FFFFFF",
          }}
        >
          <AiIcon sx={{ fontSize: 18 }} />
          <Typography sx={{ fontSize: 14, fontWeight: 700, flex: 1 }}>
            Describe your audience
          </Typography>
          <IconButton size="small" onClick={() => setChatOpen(false)} sx={{ color: "#FFFFFF" }} aria-label="Close criteria chat">
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <CampaignCriteriaChat
            domain={String(campaign?.domain || "").trim()}
            authorId={authorId}
            initialFilter={filter}
            onApply={applyChatFilter}
            onClose={() => setChatOpen(false)}
          />
        </DialogContent>
      </Dialog>
      <Dialog
        open={senderDialogOpen}
        onClose={() => {
          setSenderDialogOpen(false);
          setSenderNotice(null);
          refreshSenderAddresses(campaign?.domain);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle
          sx={{ fontSize: 15, fontWeight: 700, color: "#1A1F36", display: "flex", alignItems: "center", gap: 1 }}
        >
          <Box sx={{ flex: 1 }}>Sender addresses</Box>
          <Tooltip title="Refresh statuses">
            <IconButton
              size="small"
              onClick={() => refreshSenderAddresses(campaign?.domain)}
              aria-label="Refresh statuses"
            >
              <RefreshIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {senderAddresses.map((a) => (
              <Box key={a.email} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography sx={{ flex: 1, fontSize: 13, color: "#1A1F36", wordBreak: "break-all" }}>
                  {a.email}
                </Typography>
                <Chip
                  size="small"
                  label={a.verified ? "Verified" : "Pending"}
                  sx={
                    a.verified
                      ? { backgroundColor: "#D1FAE5", color: "#065F46", fontWeight: 700 }
                      : { backgroundColor: "#FEF3C7", color: "#92400E", fontWeight: 700 }
                  }
                />
                {!a.verified ? (
                  <Button
                    size="small"
                    disabled={senderBusy}
                    onClick={async () => {
                      setSenderBusy(true);
                      setSenderNotice(null);
                      try {
                        await apiRequestSenderVerification({ domain: campaign?.domain, email: a.email });
                        setSenderNotice({
                          severity: "success",
                          text: `Verification email sent to ${a.email} — click the link in that inbox.`,
                        });
                        await refreshSenderAddresses(campaign?.domain);
                      } catch (err) {
                        setSenderNotice({ severity: "error", text: err?.message || "Request failed" });
                      } finally {
                        setSenderBusy(false);
                      }
                    }}
                    sx={{ textTransform: "none", fontSize: 11.5, color: "#3F51B5" }}
                  >
                    Re-send
                  </Button>
                ) : null}
              </Box>
            ))}
            <Box sx={{ display: "flex", gap: 0.75, alignItems: "center", mt: senderAddresses.length ? 0.5 : 0 }}>
              <TextField
                fullWidth
                size="small"
                type="email"
                label="Email address"
                value={senderInput}
                onChange={(e) => setSenderInput(e.target.value)}
              />
              <Button
                size="small"
                variant="contained"
                disabled={senderBusy || !senderInput.trim()}
                onClick={async () => {
                  const addr = senderInput.trim();
                  setSenderBusy(true);
                  setSenderNotice(null);
                  try {
                    await apiRequestSenderVerification({ domain: campaign?.domain, email: addr });
                    setSenderInput("");
                    setSenderNotice({
                      severity: "success",
                      text: `Verification email sent to ${addr} — click the link in that inbox.`,
                    });
                    await refreshSenderAddresses(campaign?.domain);
                  } catch (err) {
                    setSenderNotice({ severity: "error", text: err?.message || "Request failed" });
                  } finally {
                    setSenderBusy(false);
                  }
                }}
                sx={{
                  textTransform: "none",
                  backgroundColor: "#3F51B5",
                  "&:hover": { backgroundColor: "#3949AB" },
                }}
              >
                Verify
              </Button>
            </Box>
            {senderNotice ? (
              <Typography
                sx={{
                  fontSize: 12,
                  color: senderNotice.severity === "error" ? "#DC2626" : "#065F46",
                }}
              >
                {senderNotice.text}
              </Typography>
            ) : null}
          </Box>
        </DialogContent>
      </Dialog>
      <Dialog open={confirmSend} onClose={() => setConfirmSend(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: 15, fontWeight: 700, color: "#1A1F36" }}>
          Send this campaign?
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13, color: "#1A1F36" }}>
            Real emails will be sent to all queued recipients of &quot;{campaign?.name || "this campaign"}&quot;.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button size="small" onClick={() => setConfirmSend(false)} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={() => {
              onSend?.(campaign);
              setConfirmSend(false);
            }}
            sx={{
              textTransform: "none",
              backgroundColor: "#3F51B5",
              "&:hover": { backgroundColor: "#3949AB" },
            }}
          >
            Send now
          </Button>
        </DialogActions>
      </Dialog>
    </AddressbookPanelShell>
  );
};

export default CampaignDetailPanel;
